import { useState } from 'react';
import { DeleteButton, EmployeePicker, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { Employee } from '../../lib/types';
import type { EosTodo } from './eosTypes';
import type { BusinessUnitOption } from './eosUi';
import {
  BusinessUnitPicker, eosToday } from './eosUi';

/**
 * To-Dos — seven-day commitments.
 *
 * The bar is 90% done by the next meeting, so the screen is built for ticking
 * rather than for reading: the checkbox is the primary control and everything
 * else is one line of context beside it. Overdue is called out because an
 * overdue to-do is the one thing on this list that needs saying out loud.
 *
 * Done ones stay visible below the line rather than disappearing. The To-Do
 * List section of an L10 is "done or not done" read off last week's list, and
 * a list that hides what was completed cannot be read that way.
 */
export function EosTodos({
  teamId,
  meetingId,
  onChanged,
}: {
  teamId: number;
  /** Set inside the meeting runner: a to-do raised in an L10 belongs to it. */
  meetingId?: number;
  onChanged: () => void;
}) {
  const todos = useResource<EosTodo[]>(`/eos/todos?teamId=${teamId}`);
  const employees = useResource<Employee[]>('/employees?status=active');
  const [title, setTitle] = useState('');
  const [ownerEmployeeId, setOwner] = useState<number | null>(null);
  const [dueDate, setDueDate] = useState('');
  const [businessUnitId, setBusinessUnit] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EosTodo | null>(null);
  const units = useResource<BusinessUnitOption[]>('/eos/business-units');

  const today = eosToday();

  const add = async () => {
    if (!title.trim()) return;
    setError(null);
    try {
      await api.post('/eos/todos', {
        teamId,
        title: title.trim(),
        ownerEmployeeId,
        dueDate: dueDate || null,
        meetingId: meetingId ?? null,
        businessUnitId,
      });
      setTitle('');
      setDueDate('');
      /* The unit is NOT cleared. Somebody adding to-dos in a meeting is
         almost always adding several for the same unit, and re-picking it
         every time is how the field stops getting used. */
      todos.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that to-do.');
    }
  };

  const toggle = async (todo: EosTodo, isDone: boolean) => {
    setError(null);
    try {
      await api.put(`/eos/todos/${todo.TodoId}`, { isDone });
      todos.reload();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update that to-do.');
    }
  };

  const open = (todos.data ?? []).filter((t) => !t.IsDone);
  const done = (todos.data ?? []).filter((t) => t.IsDone);
  const overdue = open.filter((t) => t.DueDate && t.DueDate.slice(0, 10) < today).length;

  return (
    <>
      {error && <ErrorBanner message={error} />}

      <section className="card">
        <div className="card-head row-between wrap">
          <div>
            <h3>To-Dos</h3>
            <p className="xs subtle">
              Seven-day action items. Done or not done — the bar is 90% by the next meeting.
            </p>
          </div>
          <div className="row wrap" style={{ gap: 4 }}>
            <span className={`eos-pill ${open.length === 0 ? 'good' : 'neutral'}`}>
              {open.length} open
            </span>
            {overdue > 0 && <span className="eos-pill critical">{overdue} overdue</span>}
          </div>
        </div>
        <div className="card-body">
        <form
          className="eos-quick-add"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <div className="grow">
            <input
              className="input"
              placeholder="New to-do — what needs doing"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <EmployeePicker employees={employees.data ?? []} value={ownerEmployeeId} onChange={setOwner} />
          <input
            className="input"
            type="date"
            aria-label="Due date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
          <BusinessUnitPicker
            units={units.data ?? []}
            value={businessUnitId}
            onChange={setBusinessUnit}
          />
          <button type="submit" className="btn btn-primary" disabled={!title.trim()}>
            Add
          </button>
        </form>

        {todos.loading && <Loading label="Loading to-dos" />}
        {todos.error && <ErrorBanner message={todos.error} />}

        <div className="eos-list mt-4">
          {open.length === 0 && !todos.loading && (
            <p className="muted">Nothing open. That is the target, not a bug.</p>
          )}
          {open.map((todo) => (
            <TodoRow
              key={todo.TodoId}
              todo={todo}
              today={today}
              onToggle={(next) => void toggle(todo, next)}
              onEdit={() => setEditing(todo)}
              onDelete={() => {
                void api
                  .del(`/eos/todos/${todo.TodoId}`)
                  .then(() => {
                    todos.reload();
                    onChanged();
                  })
                  .catch((err: unknown) =>
                    setError(err instanceof Error ? err.message : 'Could not delete it.'),
                  );
              }}
            />
          ))}
        </div>

        {done.length > 0 && (
          <>
            <div className="eos-section-title mt-4">Done ({done.length})</div>
            <div className="eos-list">
            {done.map((todo) => (
              <TodoRow
                key={todo.TodoId}
                todo={todo}
                today={today}
                onToggle={(next) => void toggle(todo, next)}
                onEdit={() => setEditing(todo)}
                onDelete={() => {
                  void api
                    .del(`/eos/todos/${todo.TodoId}`)
                    .then(() => {
                      todos.reload();
                      onChanged();
                    })
                    .catch((err: unknown) =>
                      setError(err instanceof Error ? err.message : 'Could not delete it.'),
                    );
                }}
              />
            ))}
            </div>
          </>
        )}
        </div>
      </section>

      {editing && (
        <EditTodoDialog
          todo={editing}
          employees={employees.data ?? []}
          units={units.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            todos.reload();
            onChanged();
          }}
        />
      )}
    </>
  );
}

function TodoRow({
  todo,
  today,
  onToggle,
  onEdit,
  onDelete,
}: {
  todo: EosTodo;
  today: string;
  onToggle: (isDone: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const late = !todo.IsDone && todo.DueDate !== null && todo.DueDate.slice(0, 10) < today;

  return (
    <div className={`eos-row${todo.IsDone ? ' is-done' : ''}`}>
      <label className="checkbox grow">
        <input type="checkbox" checked={todo.IsDone} onChange={(e) => onToggle(e.target.checked)} />
        <span>
          <span className="title">{todo.Title}</span>
          <span className="meta">
            {' · '}
            {todo.OwnerName ?? 'Unassigned'}
            {todo.DueDate && ` · due ${formatDate(todo.DueDate)}`}
          </span>
        </span>
      </label>
      {late && <span className="eos-pill critical">Overdue</span>}
      <button type="button" className="btn btn-ghost btn-sm" onClick={onEdit}>
        Edit
      </button>
      <DeleteButton label="✕" what={`the to-do "${todo.Title}"`} onConfirm={onDelete} />
    </div>
  );
}

/**
 * Editing a to-do after it was raised.
 *
 * Same reasoning as the issue dialog: the quick-add row is built for speed in
 * a meeting, so what it captures is whatever was true in the ten seconds it
 * took to type. The owner changes, the date slips, the wording was shorthand
 * only the person who said it understood. Without this the only correction
 * available was delete-and-retype, which drops the to-do off the list it was
 * raised on and out of the meeting it belongs to.
 *
 * Done is not in here. It is the checkbox — the whole screen is built around
 * that being one click — and a form that could also tick it would be a second,
 * slower way to do the one thing that is already easy.
 */
function EditTodoDialog({
  todo,
  employees,
  units,
  onClose,
  onSaved,
}: {
  todo: EosTodo;
  employees: Employee[];
  units: BusinessUnitOption[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(todo.Title);
  const [ownerEmployeeId, setOwner] = useState<number | null>(todo.OwnerEmployeeId);
  /* The column is a datetime on the way out; the input wants a bare day. */
  const [dueDate, setDueDate] = useState(todo.DueDate ? todo.DueDate.slice(0, 10) : '');
  const [businessUnitId, setBusinessUnit] = useState<number | null>(todo.BusinessUnitId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/eos/todos/${todo.TodoId}`, {
        title: title.trim(),
        ownerEmployeeId,
        dueDate: dueDate || null,
        businessUnitId,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that to-do.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit to-do"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !title.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <Field label="To-do" hint="What needs doing.">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      <div className="form-grid mt-4">
        <Field label="Owner">
          <EmployeePicker employees={employees} value={ownerEmployeeId} onChange={setOwner} />
        </Field>
        <Field label="Due">
          <input
            className="input"
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
          />
        </Field>
        <Field label="Business unit">
          <BusinessUnitPicker units={units} value={businessUnitId} onChange={setBusinessUnit} />
        </Field>
      </div>
    </Modal>
  );
}
